
import { CheckOutlined, RemoveOutlined } from "@mui/icons-material";
import { Box, Stack, Typography } from "@mui/material";

import { colors, semantic } from "../../theme/tokens";
import type { WidgetProps } from "../types";
import { formatGeneratedAt } from "./format";

// Live-data widget #3: where the supplier sells today vs. where Bison
// can extend reach. Frames gaps as growth headroom, not absence.
export function ChannelCoverage({ widget, room }: WidgetProps<"channel_coverage">) {
    const { props, data } = widget;
    const title = props.title ?? "Channel coverage";
    const supplierLabel = room.supplier.supplierName;

    return (
        <Box>
            <Typography variant="h4" sx={{ mb: 0.5 }}>
                {title}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                Snapshot · {formatGeneratedAt(data.generatedAt)}
            </Typography>

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
                    gap: 2,
                }}
            >
                {data.channels.map((c) => {
                    const status = classify(c.bisonActive, c.supplierActive);
                    const meta = STATUS_META[status];
                    return (
                        <Box
                            key={c.name}
                            sx={{
                                p: 2,
                                border: 1,
                                borderColor: "divider",
                                borderTop: 3,
                                borderTopColor: meta.accent,
                                borderRadius: "10px",
                                bgcolor: "background.paper",
                            }}
                        >
                            <Stack
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                                sx={{ mb: 1 }}
                            >
                                <Typography variant="h6" sx={{ fontSize: "1rem" }}>
                                    {c.name}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        textTransform: "uppercase",
                                        fontWeight: 600,
                                        color: meta.accent,
                                    }}
                                >
                                    {meta.label}
                                </Typography>
                            </Stack>
                            <Stack spacing={0.5}>
                                <CoverageRow label="Bison" active={c.bisonActive} />
                                <CoverageRow label={supplierLabel} active={c.supplierActive} />
                            </Stack>
                            {c.note && (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mt: 1.5, fontSize: "0.8125rem" }}
                                >
                                    {c.note}
                                </Typography>
                            )}
                            {(c.evidence || c.observedStatus) && (
                                <Box
                                    sx={{
                                        mt: 1.5,
                                        pt: 1.25,
                                        borderTop: 1,
                                        borderColor: "divider",
                                    }}
                                >
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: "block", fontSize: "0.7rem" }}
                                    >
                                        {c.observedStatus
                                            ? OBSERVED_LABEL[c.observedStatus]
                                            : "What we saw"}
                                        {c.checkedAt
                                            ? ` · ${formatGeneratedAt(c.checkedAt)}`
                                            : ""}
                                    </Typography>
                                    {c.evidence && (
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            sx={{
                                                display: "block",
                                                fontSize: "0.7rem",
                                                mt: 0.25,
                                            }}
                                        >
                                            {c.evidence}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

function CoverageRow({ label, active }: { label: string; active: boolean }) {
    return (
        <Stack direction="row" spacing={1} alignItems="center">
            <Box
                sx={{
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    bgcolor: active ? "primary.main" : "grey.100",
                    color: active ? "common.white" : "text.secondary",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.875rem",
                }}
            >
                {active ? <CheckOutlined fontSize="inherit" /> : <RemoveOutlined fontSize="inherit" />}
            </Box>
            <Typography variant="body2" color="text.secondary">
                {label}
            </Typography>
        </Stack>
    );
}

type Status = "covered" | "headroom" | "expand" | "untouched";

function classify(bison: boolean, supplier: boolean): Status {
    if (bison && supplier) return "covered";
    if (bison && !supplier) return "headroom"; // Bison can launch the supplier here
    if (!bison && supplier) return "expand"; // Supplier here, Bison not yet
    return "untouched";
}

// What the check actually returned. "Nothing came back from a search" and
// "the site would not let us look" are different facts, and neither of them is
// the same as an unchecked blank — a supplier reading this should be able to
// see which one applies. Added 2026-09-03.
const OBSERVED_LABEL: Record<
    NonNullable<
        WidgetProps<"channel_coverage">["widget"]["data"]["channels"][number]["observedStatus"]
    >,
    string
> = {
    listed: "Checked: found listings",
    not_found_in_search: "Checked: search returned nothing",
    not_observable: "Could not check",
};

const STATUS_META: Record<Status, { label: string; accent: string }> = {
    covered: { label: "Covered", accent: semantic.info.text },
    headroom: { label: "Headroom", accent: colors.brandOrange },
    expand: { label: "Expand together", accent: semantic.info.border },
    untouched: { label: "New channel", accent: colors.mediumGray },
};
